import React, { PropsWithChildren, useMemo, useState } from "react";
import { useForumsNavigation } from "../../../hooks/useForumNavigation";
import { buildPath, ROUTE_PARAMS, ROUTE_PATH } from "../../../utilities/navigation";
import { useParams } from "react-router";
import { ProfileDetails } from "../../../components/profile/ProfileDetails";
import { useQuery } from "react-query";
import { getUserPublicInfo } from "../../../services/user";
import { Avatar, Divider, MenuItem, Select, Skeleton, Stack, Typography } from "@mui/material";
import { PropsWithLoadingState } from "../../../utilities";
import { getUserPostsList, getUserRepliesList } from "../../../services/forums";
import { ForumPost, ForumReply, WithID } from "../../../types/db";
import { ForumPostItem } from "../../../components/forums/ForumPostItem/ForumPostItem";
import { ForumPostReplyItem } from "../../../components/forums/ForumPostReplyItem/ForumPostReplyItem";
import { mapToPostItem } from "../../../containers/forums/ForumsTopicPostsContainer";
import { mapToReplyItem } from "../../../containers/forums/ForumReplyContainer";
import { Comment, Forum, Mood, TextSnippet, ThumbUp } from "@mui/icons-material";
import { WEBSITE_BASE_URL } from "../../../types/constants";
import { useTranslation } from "react-i18next";

export const ForumsUserPage = () => {
    const { [ROUTE_PARAMS.userID]: userId } = useParams();
    const { navigateToPost } = useForumsNavigation();

    if (!userId) {
        return <ProfileDetails error />
    }

    return <ForumsUserDetailsContainer
        userId={userId}
        navigateToPost={navigateToPost} />;
}

interface IForumsUserDetailsContainerProps {
    /** The id of the user to display */
    userId: string;
    /** Callback navigate to a forum post */
    navigateToPost: (postId: string, replyId?: string) => void;
}

const ForumsUserDetailsContainer = ({ userId, navigateToPost }: IForumsUserDetailsContainerProps) => {
    const { isLoading, isError, data } = useQuery(
        ['ProfileDetails', userId],
        () => getUserPublicInfo(userId),
    );

    const [typeActivity, setTypeActivity] = useState<TActivityType>('posts');

    const props: IForumsUserDetailsScreenProps = useMemo(() => {
        if (!data) return {} as IForumsUserDetailsScreenProps;
        return {
            user: {
                displayName: data.display_name,
                avatarUrl: data.avatar_url,
                memberSince: data.join_date.toDate(),
                activities: {
                    postsCount: data.forums.posts_count,
                    repliesCount: data.forums.replies_count,
                    likesCount: data.forums.likes_count,
                    repliesReceivedCount: data.forums.replied_count,
                    likesReceivedCount: data.forums.liked_count,
                },
                onClick: () => window.open(buildPath(ROUTE_PATH.userBuild(userId), WEBSITE_BASE_URL), '_blank')
            },
            typeActivity,
            onTypeActivityChange: setTypeActivity
        }
    }, [data, typeActivity, userId]);

    if (isError) {
        return null;//TODO
    }

    return <ForumsUserDetailsScreen
        {...props}
        loading={isLoading}
    >
        <ForumsUserActivityContainer {...{ userId, navigateToPost }} type={typeActivity} />
    </ForumsUserDetailsScreen>
}

interface IForumsUserDetailsScreenProps {
    /** The forum member */
    user: {
        displayName: string;
        avatarUrl: string;
        memberSince: Date;
        activities: {
            postsCount: number;
            repliesCount: number;
            likesCount: number;
            repliesReceivedCount: number;
            likesReceivedCount: number;
        };
        onClick: () => void;
    }
    /** The type of activity */
    typeActivity: TActivityType;
    /** The callback on type change */
    onTypeActivityChange: (newType: TActivityType) => void;
}
const ForumsUserDetailsScreen = ({ user, loading, children, typeActivity, onTypeActivityChange }: PropsWithLoadingState<PropsWithChildren<IForumsUserDetailsScreenProps>>) => {
    const { t } = useTranslation();

    return <Stack direction={'column'} spacing={2} p={5}>
        <Stack direction={'row'} spacing={2} alignItems={'center'}>
            {!loading ? <Avatar src={user.avatarUrl} alt={user.displayName} onClick={user.onClick} /> : <Skeleton variant={"circular"}><Avatar /></Skeleton>}
            <Stack direction={'column'} spacing={1}>
                <Typography minWidth={50} onClick={user?.onClick}>{!loading ? t('forum.user.profileTitle', { name: user.displayName }) : <Skeleton />}</Typography>
                <Typography minWidth={50} variant="caption">{!loading ? t('forum.user.memberSince', { date: user.memberSince.toDateString() }) : <Skeleton />}</Typography>
            </Stack>
        </Stack>
        <Stack direction={'row'} flexWrap={'wrap'} justifyContent={'space-around'} alignItems={'center'} spacing={1}>
            <Stack direction={'row'} gap={1}><TextSnippet /><Typography minWidth={50}>{!loading ? t('forum.user.postsCount', { count: user.activities.postsCount }) : <Skeleton />}</Typography></Stack>
            <Stack direction={'row'} gap={1}><Comment /><Typography minWidth={50}>{!loading ? t('forum.user.repliesGivenCount', { count: user.activities.repliesCount }) : <Skeleton />}</Typography></Stack>
            <Stack direction={'row'} gap={1}><Forum /><Typography minWidth={50}>{!loading ? t('forum.user.repliesReceivedCount', { count: user.activities.repliesReceivedCount }) : <Skeleton />}</Typography></Stack>
            <Stack direction={'row'} gap={1}><ThumbUp /><Typography minWidth={50}>{!loading ? t('forum.user.likesGivenCount', { count: user.activities.likesCount }) : <Skeleton />}</Typography></Stack>
            <Stack direction={'row'} gap={1}><Mood /><Typography minWidth={50}>{!loading ? t('forum.user.likesReceivedCount', { count: user.activities.likesReceivedCount }) : <Skeleton />}</Typography></Stack>
        </Stack>
        <Divider />
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
            <Typography variant="h3">{t('forum.user.activityTitle')}</Typography>
            {!loading &&
                <Select
                    variant="outlined"
                    size="small"
                    value={typeActivity}
                    onChange={(e) => {
                        onTypeActivityChange(e.target.value as TActivityType);
                    }}
                    renderValue={(selected) => selected}
                >
                    <MenuItem value={'posts'}>{t('forum.user.activityType', { context: 'posts' })}</MenuItem>
                    <MenuItem value={'replies'}>{t('forum.user.activityType', { context: 'replies' })}</MenuItem>
                </Select>
            }
        </Stack>
        {children}
    </Stack>
}

type TActivityType = 'posts' | 'replies';

interface IForumsUserActivityContainerProps extends IForumsUserDetailsContainerProps {
    /** The type of activity*/
    type: TActivityType;
}
/** Takes care of fetching all user activities depending on type (posts or replies) */
const ForumsUserActivityContainer = ({ userId, type, navigateToPost }: IForumsUserActivityContainerProps) => {
    const { isLoading, isError, data } = useQuery<WithID<ForumPost | ForumReply>[]>(
        ['ProfileDetails', userId, "forumActivity", type],
        () => type === 'posts' ? getUserPostsList(userId)
            : type === 'replies' ? getUserRepliesList(userId)
                : Promise.resolve([]),
        {
            refetchOnWindowFocus: false,
            enabled: !!type
        });

    if (isError) {
        return null;//TODO
    }

    return <>
        {
            !isLoading ?
                (data || []).map(value => {
                    switch (type) {
                        case 'posts':
                            const postValue = value as WithID<ForumPost>;
                            return <ForumPostItem key={value.id} {...mapToPostItem(postValue, () => navigateToPost(postValue.id))} />
                        case 'replies':
                            const replyValue = value as WithID<ForumReply>
                            return <ForumPostReplyItem key={value.id} {...mapToReplyItem(replyValue, () => navigateToPost(replyValue.post_id, replyValue.id))} />
                        default:
                            return null;
                    }
                })
                : new Array(5).fill({}).map((_, index) => {
                    switch (type) {
                        case 'posts':
                            return <ForumPostItem key={`loading-${index}`} loading />
                        case 'replies':
                            return <ForumPostReplyItem key={`loading-${index}`} loading />
                        default:
                            return null;
                    }
                })
        }
    </>
}